/**
 * Which metrics a formula ranks by the lowest (02/11/2026): the ones picked
 * "Lowest" in sliders and every `lowest(m)` in blocks or sub-formulas — the
 * ranker gives each one its max. Plus the metrics where less is better (a
 * cost, a bad rate), which the editor pre-sets to "Lowest" when picked.
 * For Media Devoted | Created By Ciro Autiero
 */
import { parseFormula, type AstNode } from "../ast";
import type { FormulaGroup } from "../../voluum-flow-optimizer.types";
import { resolveEffectiveExpression } from "../scoring/score-group";
import { LOWEST_FN } from "./lowest";

/** Costs and bad rates: picking one starts it on "Lowest". */
export const LOWER_IS_BETTER_METRICS: ReadonlySet<string> = new Set([
  "cost",
  "cpc",
  "cpv",
  "CPuC",
  "CPL",
  "CPS",
  "ecpa",
  "botRate",
  "undeliverableRate",
]);

export const isLowerBetter = (metric: string): boolean =>
  LOWER_IS_BETTER_METRICS.has(metric);

type LowestGroup = Pick<FormulaGroup, "mode" | "sliders" | "blocks" | "expression">;

/** The metrics the group reads through `lowest()` — its formula and sub-formulas. */
export function lowestMetricsOf(group: LowestGroup): string[] {
  const subs =
    group.mode === "blocks"
      ? (group.blocks?.subformulas ?? []).map((s) => s.expression ?? "")
      : [];
  const out = new Set<string>();
  for (const source of [resolveEffectiveExpression(group), ...subs])
    if (source.includes(LOWEST_FN)) collect(parsed(source), out);
  return [...out];
}

function parsed(source: string): AstNode | null {
  try {
    return parseFormula(source);
  } catch {
    return null;
  }
}

function collect(node: AstNode | null, out: Set<string>): void {
  if (!node || node.kind === "num" || node.kind === "ident") return;
  if (node.kind === "unary") return collect(node.operand, out);
  if (node.kind === "binary") {
    collect(node.left, out);
    return collect(node.right, out);
  }
  const arg = node.args[0];
  if (node.name === LOWEST_FN && arg?.kind === "ident") out.add(arg.name);
  else for (const a of node.args) collect(a, out);
}
